import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, LogOut } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const { count, setIsOpen } = useCart();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#f9f6f0]/80 border-b border-[#e6e2d8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-brand-link"
          className="font-serif text-3xl tracking-tight text-[#1e392a]"
        >
          ARIALOK
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                "text-sm tracking-wide transition-colors " +
                (isActive
                  ? "text-[#1e392a] font-semibold"
                  : "text-[#1a1b1a]/70 hover:text-[#1e392a]")
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="navbar-account-btn"
                className="p-2 rounded-full hover:bg-[#efeadd] transition-colors"
                aria-label="Account"
              >
                <User className="h-5 w-5 text-[#1e392a]" strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-white border border-[#e6e2d8]"
            >
              {user ? (
                <>
                  <DropdownMenuLabel className="font-normal">
                    <p className="text-xs text-[#8a8d86]">Signed in as</p>
                    <p className="text-sm font-medium truncate">{user.email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="menu-account"
                    onClick={() => navigate("/account")}
                  >
                    My Account
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem
                      data-testid="menu-admin"
                      onClick={() => navigate("/admin")}
                    >
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="menu-logout"
                    onClick={async () => {
                      await logout();
                      navigate("/");
                    }}
                  >
                    <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Sign out
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem
                    data-testid="menu-login"
                    onClick={() => navigate("/login")}
                  >
                    Sign in
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-register"
                    onClick={() => navigate("/register")}
                  >
                    Create account
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="navbar-cart-btn"
            onClick={() => setIsOpen(true)}
            className="relative p-2 rounded-full hover:bg-[#efeadd] transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag className="h-5 w-5 text-[#1e392a]" strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 bg-[#c36a4f] text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-medium"
              >
                {count}
              </span>
            )}
          </button>

          <button
            data-testid="navbar-mobile-menu-btn"
            className="md:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#e6e2d8] bg-[#f9f6f0]">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="text-base text-[#1a1b1a]"
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
